import { useSelector, useDispatch } from 'react-redux';
import { 
  selectCustomRoles, 
  setCustomRoles,
  addCustomRole, 
  updateCustomRole, 
  deleteCustomRole 
} from '../redux/slices/customRolesSlice';

/**
 * Helper function to normalize email addresses
 * This ensures consistent comparison regardless of case or whitespace
 */
const normalizeEmail = (email) => {
  if (!email) return '';
  return email.trim().toLowerCase();
};

/**
 * Function to get custom roles from Redux store
 * This is a non-hook version for use in non-component code
 */
export const getCustomRoles = (userEmail, state) => {
  if (!userEmail) return null;
  
  const normalizedEmail = normalizeEmail(userEmail);
  
  // If state is provided, use it directly (useful for server-side or outside components)
  if (state && state.customRoles?.roles) {
    // Try direct lookup first
    if (state.customRoles.roles[normalizedEmail]) {
      return state.customRoles.roles[normalizedEmail];
    }
    
    // Try case-insensitive search as fallback
    const emails = Object.keys(state.customRoles.roles);
    const matchingEmail = emails.find(email => 
      normalizeEmail(email) === normalizedEmail
    );
    
    if (matchingEmail) {
      return state.customRoles.roles[matchingEmail];
    }
    
    return null;
  }

  // Fallback to static mappings if no state is provided
  // This should primarily be used outside of components or before Redux is initialized
  const staticRoleMappings = {
    "holli.fletcher@pumpservicenetwork.com": "PSN",
    "tommy.kosek@pumpservicenetwork.com": "PSN",
    "ryan.schechter@pumpservicenetwork.com": "PSN",
  };
  
  // Check normalized email against static mappings (case insensitive)
  for (const [email, role] of Object.entries(staticRoleMappings)) {
    if (normalizeEmail(email) === normalizedEmail) {
      return role;
    }
  }
  
  return null;
};

/**
 * Hook to access and manage custom roles from Redux store
 * This should be used in React components
 */
export const useCustomRoles = () => {
  const customRoles = useSelector(selectCustomRoles);
  const dispatch = useDispatch();
  
  return {
    // Get a specific user's role with normalized email comparison
    getRole: (userEmail) => {
      if (!userEmail) return null;
      
      const normalizedEmail = normalizeEmail(userEmail);
      
      // Try direct lookup first
      if (customRoles[normalizedEmail]) {
        return customRoles[normalizedEmail];
      }
      
      // Try case-insensitive search as fallback
      const emails = Object.keys(customRoles);
      const matchingEmail = emails.find(email => 
        normalizeEmail(email) === normalizedEmail
      );
      
      if (matchingEmail) {
        return customRoles[matchingEmail];
      }
      
      return null;
    },
    
    // Get all custom roles
    getAllRoles: () => customRoles,
    
    // Set all custom roles at once
    setRoles: (rolesObject) => {
      dispatch(setCustomRoles(rolesObject));
    },
    
    // Add a new custom role with normalized email
    addRole: (email, role) => {
      dispatch(addCustomRole({ email: normalizeEmail(email), role }));
    },
    
    // Update an existing custom role
    updateRole: (email, role) => {
      dispatch(updateCustomRole({ email: normalizeEmail(email), role }));
    },
    
    // Delete a custom role
    deleteRole: (email) => {
      dispatch(deleteCustomRole(normalizeEmail(email)));
    },
  };
};
